// src/components/ComparisonSelector.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Upload, FolderOpen, Search, FileText, 
  GitCompare, X, ChevronRight, Clock,
  File, Folder, ArrowRight, GitCommit,
  GitBranch, Calendar, User
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import TxtRayDiffViewer from './TxtRayDiffViewer';
import './ComparisonSelector.css';

interface FileItem {
  name: string;
  path: string;
  content: string;
  size?: number;
  modified?: Date;
  type?: 'file' | 'directory' | 'version';
  commitHash?: string;
  commitMessage?: string;
  author?: string;
  date?: string;
}

interface ComparisonSelectorProps {
  onClose?: () => void;
  currentDocument?: FileItem | null;
  recentFiles?: FileItem[];
  isGitRepo?: boolean;
}

const ComparisonSelector: React.FC<ComparisonSelectorProps> = ({
  onClose,
  currentDocument,
  recentFiles = [],
  isGitRepo = false
}) => {
  const [leftFile, setLeftFile] = useState<FileItem | null>(currentDocument || null);
  const [rightFile, setRightFile] = useState<FileItem | null>(null);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingRight, setIsDraggingRight] = useState(false);
  const [directoryFiles, setDirectoryFiles] = useState<FileItem[]>([]);
  const [isLoadingLeft, setIsLoadingLeft] = useState(false);
  const [isLoadingRight, setIsLoadingRight] = useState(false);
  const [fileHistory, setFileHistory] = useState<FileItem[]>([]);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);

  const leftFileInputRef = useRef<HTMLInputElement>(null);
  const rightFileInputRef = useRef<HTMLInputElement>(null);

  // Load git history for a file
  const loadFileHistory = async (file: FileItem) => {
    if (!isGitRepo) {
      console.log('Not a git repository');
      alert('This is not a git repository');
      return;
    }
    
    if (!file.path) {
      console.log('No file path available');
      alert('No file path available');
      return;
    }
    
    try {
      console.log('Loading git history for file:', {
        name: file.name,
        path: file.path,
        fullPath: file.path
      });
      
      setSelectedFile(file);
      
      // Get git history for the file - returns an array of commits
      const history = await window.electronAPI.gitFileHistory(file.path);
      console.log('Git history API response:', history);
      
      if (history && Array.isArray(history) && history.length > 0) {
        const versions = history.map((commit: any) => ({
          name: `${file.name} @ ${commit.hash.substring(0, 7)}`,
          path: file.path,
          content: '', // Will be loaded when selected
          type: 'version' as const,
          commitHash: commit.hash,
          commitMessage: commit.message,
          author: commit.author,
          date: commit.date
        }));
        
        // Add current version at the top
        versions.unshift({
          name: `${file.name} (Current)`,
          path: file.path,
          content: file.content,
          type: 'version' as const,
          commitHash: 'HEAD',
          commitMessage: 'Current version',
          author: 'You',
          date: new Date().toISOString()
        });
        
        setFileHistory(versions);
        setShowVersionSelector(true);
      } else {
        console.log('No git history found for file, showing current version only');
        // If no history, still show selector with just current version
        setFileHistory([{
          name: `${file.name} (Current)`,
          path: file.path,
          content: file.content,
          type: 'version' as const,
          commitHash: 'HEAD',
          commitMessage: 'Current version',
          author: 'You',
          date: new Date().toISOString()
        }]);
        setShowVersionSelector(true);
      }
    } catch (error) {
      console.error('Error loading file history:', error);
      alert(`Failed to load git history: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Load historical content
  const loadHistoricalContent = async (version: FileItem) => {
    if (!version.commitHash || version.commitHash === 'HEAD') {
      return version;
    }
    
    try {
      const result = await window.electronAPI.gitShowFile(version.path, version.commitHash);
      if (result.success) {
        return {
          ...version,
          content: result.content
        };
      }
    } catch (error) {
      console.error('Error loading historical content:', error);
    }
    
    return version;
  };

  // Handle file selection from file input
  const handleFileSelect = async (file: File, side: 'left' | 'right') => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const fileItem: FileItem = {
        name: file.name,
        path: file.webkitRelativePath || file.name, // Try to get full path if available
        content: content,
        size: file.size,
        modified: new Date(file.lastModified)
      };
      
      if (side === 'left') {
        setLeftFile(fileItem);
      } else {
        setRightFile(fileItem);
      }
    };
    reader.readAsText(file);
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent, side: 'left' | 'right') => {
    e.preventDefault();
    e.stopPropagation();
    if (side === 'left') {
      setIsDraggingLeft(true);
    } else {
      setIsDraggingRight(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent, side: 'left' | 'right') => {
    e.preventDefault();
    e.stopPropagation();
    if (side === 'left') {
      setIsDraggingLeft(false);
    } else {
      setIsDraggingRight(false);
    }
  };

  const handleDrop = async (e: React.DragEvent, side: 'left' | 'right') => {
    e.preventDefault();
    e.stopPropagation();
    
    if (side === 'left') {
      setIsDraggingLeft(false);
    } else {
      setIsDraggingRight(false);
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0], side);
    }
  };

  // Load directory files
  const loadDirectory = async (side: 'left' | 'right') => {
    try {
      if (side === 'left') {
        setIsLoadingLeft(true);
      } else {
        setIsLoadingRight(true);
      }

      // Request directory from Electron
      const result = await window.electronAPI.selectDirectory();
      if (result && result.path) {
        const docs = await window.electronAPI.listDocuments();
        const filesWithContent = await Promise.all(
          docs.map(async (doc: any) => {
            const contentResult = await window.electronAPI.readDocument(doc.path);
            return {
              name: doc.name,
              path: doc.path,
              content: contentResult.success ? contentResult.content : '',
              size: doc.size,
              modified: new Date(doc.modified),
              type: 'file' as const
            };
          })
        );
        setDirectoryFiles(filesWithContent);
      }
    } catch (error) {
      console.error('Error loading directory:', error);
    } finally {
      if (side === 'left') {
        setIsLoadingLeft(false);
      } else {
        setIsLoadingRight(false);
      }
    }
  };

  // Load files on mount if we have recent files
  useEffect(() => {
    // If we have recent files with paths, they're probably from the current directory
    if (recentFiles.length > 0) {
      setDirectoryFiles(recentFiles);
    }
  }, [recentFiles]);

  // Filter files based on search
  const getFilteredFiles = (searchTerm: string) => {
    const allFiles = [...new Set([...directoryFiles, ...recentFiles])]; // Remove duplicates
    if (!searchTerm) return allFiles.slice(0, 10); // Show first 10 files
    
    return allFiles.filter(file => 
      file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.path.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const leftFilteredFiles = getFilteredFiles(leftSearch);
  const rightFilteredFiles = getFilteredFiles(rightSearch);

  // Start comparison
  const startComparison = () => {
    if (leftFile && rightFile) {
      setShowComparison(true);
    }
  };

  // Render file selector panel
  const renderFilePanel = (
    side: 'left' | 'right',
    file: FileItem | null,
    setFile: (file: FileItem | null) => void,
    search: string,
    setSearch: (search: string) => void,
    filteredFiles: FileItem[],
    isDragging: boolean,
    isLoading: boolean,
    fileInputRef: React.RefObject<HTMLInputElement>
  ) => {
    const borderColor = side === 'left' ? '#4a9eff' : '#22c55e';
    const bgColor = side === 'left' ? 'rgba(74, 158, 255, 0.05)' : 'rgba(34, 197, 94, 0.05)';
    
    return (
      <div 
        className={`file-panel ${side} ${isDragging ? 'dragging' : ''}`}
        style={{ borderColor: isDragging ? borderColor : undefined }}
        onDragOver={handleDragOver}
        onDragEnter={(e) => handleDragEnter(e, side)}
        onDragLeave={(e) => handleDragLeave(e, side)}
        onDrop={(e) => handleDrop(e, side)}
      >
        <div className="panel-header">
          <h3>
            {side === 'left' ? 'Original' : 'Modified'}
            {file && <span className="file-count"> • {file.name}</span>}
          </h3>
          {file && (
            <button 
              className="clear-btn"
              onClick={() => setFile(null)}
              title="Clear selection"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {!file ? (
          <div className="file-selector">
            <div 
              className="drop-zone"
              style={{ backgroundColor: isDragging ? bgColor : undefined }}
            >
              <Upload size={48} />
              <p>Drag & drop a file here</p>
              <span>or</span>
              <div className="action-buttons">
                <button 
                  className="action-btn primary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <File size={18} />
                  Choose File
                </button>
                <button 
                  className="action-btn"
                  onClick={() => loadDirectory(side)}
                  disabled={isLoading}
                >
                  <FolderOpen size={18} />
                  Browse Directory
                </button>
              </div>
            </div>

            <div className="search-section">
              <div className="search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="file-list">
                {filteredFiles.length > 0 ? (
                  <>
                    <div className="list-header">
                      {search ? 'Search Results' : 'Recent Files'}
                    </div>
                    {filteredFiles.map((fileItem, idx) => (
                      <div
                        key={idx}
                        className="file-item"
                      >
                        <FileText size={16} />
                        <div className="file-info" onClick={() => setFile(fileItem)}>
                          <span className="file-name">{fileItem.name}</span>
                          <span className="file-path">{fileItem.path}</span>
                        </div>
                        {isGitRepo && fileItem.path && fileItem.path.includes('/') && (
                          <button
                            className="git-history-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFile(fileItem);
                              loadFileHistory(fileItem);
                            }}
                            title="Load git versions of this file"
                          >
                            <Clock size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="empty-state">
                    <Folder size={32} />
                    <p>No files found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="file-preview">
            <div className="preview-header">
              <FileText size={20} />
              <div className="preview-info">
                <h4>{file.name}</h4>
                <span className="preview-path">{file.path}</span>
                {file.size && (
                  <span className="preview-meta">
                    {(file.size / 1024).toFixed(1)} KB
                    {file.modified && ` • ${file.modified.toLocaleDateString()}`}
                  </span>
                )}
              </div>
            </div>
            <div className="preview-content">
              <pre>{file.content.substring(0, 500)}...</pre>
            </div>
            <div className="preview-actions">
              <button 
                className="change-file-btn"
                onClick={() => setFile(null)}
              >
                Choose Different File
              </button>
              {isGitRepo && file.path && file.path.includes('/') && (
                <button 
                  className="git-history-preview-btn"
                  onClick={() => {
                    loadFileHistory(file);
                  }}
                  title="Compare different versions of this file from git history"
                >
                  <GitBranch size={16} />
                  Compare Git Versions
                </button>
              )}
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file, side);
          }}
          accept=".txt,.md,.js,.jsx,.ts,.tsx,.json,.css,.html,.xml,.yaml,.yml"
        />
      </div>
    );
  };

  if (showComparison && leftFile && rightFile) {
    return (
      <TxtRayDiffViewer
        text1={leftFile.content}
        text2={rightFile.content}
        title1={leftFile.name}
        title2={rightFile.name}
        onClose={() => setShowComparison(false)}
        className="comparison-viewer"
      />
    );
  }

  // Version selector modal
  if (showVersionSelector && selectedFile && fileHistory.length > 0) {
    return (
      <div className="comparison-selector">
        <div className="selector-header">
          <h2>
            <GitCommit size={24} />
            Select Versions to Compare
          </h2>
          <button className="close-btn" onClick={() => setShowVersionSelector(false)}>
            <X size={20} />
          </button>
        </div>
        
        <div className="version-selector-content">
          <h3 className="file-name-header">
            <FileText size={18} />
            {selectedFile?.name || 'File History'}
          </h3>
          
          <div className="version-grid">
            <div className="version-column">
              <h4>Select First Version</h4>
              <div className="version-list">
                {fileHistory.map((version, idx) => (
                  <div
                    key={idx}
                    className={`version-item ${leftFile?.commitHash === version.commitHash ? 'selected' : ''}`}
                    onClick={async () => {
                      const versionWithContent = await loadHistoricalContent(version);
                      setLeftFile(versionWithContent);
                    }}
                  >
                    <div className="version-header">
                      <GitCommit size={16} />
                      <span className="version-name">{version.name}</span>
                    </div>
                    <div className="version-info">
                      <span className="commit-message">{version.commitMessage}</span>
                      <div className="version-meta">
                        <User size={12} />
                        <span>{version.author}</span>
                        <Calendar size={12} />
                        <span>{new Date(version.date || '').toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="version-divider">
              <ArrowRight size={24} />
            </div>
            
            <div className="version-column">
              <h4>Select Second Version</h4>
              <div className="version-list">
                {fileHistory.map((version, idx) => (
                  <div
                    key={idx}
                    className={`version-item ${rightFile?.commitHash === version.commitHash ? 'selected' : ''}`}
                    onClick={async () => {
                      const versionWithContent = await loadHistoricalContent(version);
                      setRightFile(versionWithContent);
                    }}
                  >
                    <div className="version-header">
                      <GitCommit size={16} />
                      <span className="version-name">{version.name}</span>
                    </div>
                    <div className="version-info">
                      <span className="commit-message">{version.commitMessage}</span>
                      <div className="version-meta">
                        <User size={12} />
                        <span>{version.author}</span>
                        <Calendar size={12} />
                        <span>{new Date(version.date || '').toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="version-selector-footer">
            <button 
              className="btn-secondary"
              onClick={() => setShowVersionSelector(false)}
            >
              Cancel
            </button>
            <button 
              className="compare-btn"
              onClick={() => {
                setShowVersionSelector(false);
                startComparison();
              }}
              disabled={!leftFile || !rightFile}
            >
              <GitCompare size={18} />
              Compare Selected Versions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="comparison-selector">
      <div className="selector-header">
        <h2>
          <GitCompare size={24} />
          Select Files to Compare
        </h2>
        {onClose && (
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        )}
      </div>

      <div className="selector-content">
        <div className="panels-container">
          {renderFilePanel(
            'left',
            leftFile,
            setLeftFile,
            leftSearch,
            setLeftSearch,
            leftFilteredFiles,
            isDraggingLeft,
            isLoadingLeft,
            leftFileInputRef
          )}

          <div className="panel-divider">
            <ArrowRight size={24} />
          </div>

          {renderFilePanel(
            'right',
            rightFile,
            setRightFile,
            rightSearch,
            setRightSearch,
            rightFilteredFiles,
            isDraggingRight,
            isLoadingRight,
            rightFileInputRef
          )}
        </div>
      </div>

      <div className="selector-footer">
        <div className="selection-status">
          {leftFile && rightFile ? (
            <span className="ready">Ready to compare</span>
          ) : (
            <span className="pending">
              Select {!leftFile && !rightFile ? 'two files' : 'another file'} to compare
            </span>
          )}
        </div>
        <button 
          className="compare-btn"
          onClick={startComparison}
          disabled={!leftFile || !rightFile}
        >
          <GitCompare size={18} />
          Compare Files
        </button>
      </div>
    </div>
  );
};

export default ComparisonSelector;